import { object, string, bool, array } from 'yup';

export const ORDER_TYPE_ATONCE = 'atonce';
export const ORDER_TYPE_FUTURES = 'futures';
export const ORDER_TYPE_FORECAST = 'forecast';

export const ORDER_TYPE_NAMES = {
  ATONCE: ORDER_TYPE_ATONCE.toUpperCase(),
  FUTURES: ORDER_TYPE_FUTURES.toUpperCase(),
  FORECAST: ORDER_TYPE_FORECAST.toUpperCase()
};

export const ORDER_TYPE_DISPLAY_NAMES = {
  ATONCE: 'At-Once',
  FUTURES: 'Futures',
  FORECAST: 'Forecast'
};

export const ORDER_TYPES = [
  { label: ORDER_TYPE_DISPLAY_NAMES.ATONCE, value: ORDER_TYPE_NAMES.ATONCE },
  { label: ORDER_TYPE_DISPLAY_NAMES.FUTURES, value: ORDER_TYPE_NAMES.FUTURES },
  { label: ORDER_TYPE_DISPLAY_NAMES.FORECAST, value: ORDER_TYPE_NAMES.FORECAST }
];

export const getOrderTypeLabel = orderType =>
  ORDER_TYPES.find(typeObj => typeObj.value.toLowerCase() === orderType.toLowerCase())?.label || orderType;

export const DEFAULT_CATALOG_STRUCTURE = {
  id: '',
  name: '',
  groupName: '',
  atOnceStart: '',
  atOnceEnd: '',
  futuresCrd: '',
  futuresStart: '',
  futuresEnd: '',
  forecastCrd: '',
  forecastStart: '',
  forecastEnd: '',
  orderTypes: [],
  owner: '',
  ownerId: '',
  directShip: false,
  createTimestamp: '',
  products: [],
  catalogTypes: [],
  salesOrg: '',
  seasons: [],
  singleSeason: false,
  status: ''
};

export function getInitialValues(currentCatalog) {
  return { ...DEFAULT_CATALOG_STRUCTURE, ...currentCatalog, singleSeason: currentCatalog?.seasons?.length === 1 };
}

export const ERROR_MESSAGES = {
  atOnceStart: 'Please enter an at-once start',
  atOnceEnd: 'Please enter an at-once end',
  groupName: 'Please enter a group name',
  name: 'Please enter a catalog name',
  catalogType: 'Please select a catalog type',
  forecastCRD: 'Please enter a forecast end',
  forecastStart: 'Please enter a forecast start',
  forecastEnd: 'Please enter a forecast end',
  futuresCRD: 'Please enter a futures CRD',
  futuresStart: 'Please enter a futures start',
  futuresEnd: 'Please enter a futures end',
  orderTypes: 'Please enter an order type',
  ownerId: 'Please select an owner',
  salesOrg: 'Please select a sales organization',
  season: 'Please select a season',
  seasonsRange: 'Please select a season range',
  dateRange: 'End date should be after start date'
};

const {
  atOnceStart,
  atOnceEnd,
  groupName,
  name,
  catalogType,
  forecastCRD,
  forecastStart,
  forecastEnd,
  futuresCRD,
  futuresStart,
  futuresEnd,
  orderTypes,
  ownerId,
  salesOrg,
  season,
  seasonsRange,
  dateRange
} = ERROR_MESSAGES;

export const validationSchema = object().shape({
  salesOrg: string().required(salesOrg),
  groupName: string().required(groupName),
  seasons: array()
    .required(season)
    .when('singleSeason', {
      is: singleSeason => !singleSeason,
      then: array().min(2, seasonsRange)
    })
    .when('singleSeason', {
      is: true,
      then: array().min(1, season).max(1, season)
    }),
  catalogTypes: array().min(1, catalogType),
  name: string().required(name),
  directShip: bool(),
  orderTypes: array().min(1, orderTypes),
  owner: string(),
  ownerId: string().required(ownerId),
  products: array(),
  atOnceStart: string().when('orderTypes', {
    is: orderTypes => orderTypes.includes(ORDER_TYPE_NAMES.ATONCE),
    then: string().required(atOnceStart)
  }),
  atOnceEnd: string()
    .when('orderTypes', {
      is: orderTypes => orderTypes.includes(ORDER_TYPE_NAMES.ATONCE),
      then: string().required(atOnceEnd)
    })
    .test('atOnceEnd', ERROR_MESSAGES.dateRange, (value, context) =>
      context?.parent?.orderTypes.includes(ORDER_TYPE_NAMES.ATONCE) ? context?.parent?.atOnceStart <= value : true
    ),
  futuresCrd: string().when('orderTypes', {
    is: orderTypes => orderTypes.includes(ORDER_TYPE_NAMES.FUTURES),
    then: string().required(futuresCRD)
  }),
  futuresStart: string().when('orderTypes', {
    is: orderTypes => orderTypes.includes(ORDER_TYPE_NAMES.FUTURES),
    then: string().required(futuresStart)
  }),
  futuresEnd: string()
    .when('orderTypes', {
      is: orderTypes => orderTypes.includes(ORDER_TYPE_NAMES.FUTURES),
      then: string().required(futuresEnd)
    })
    .test('futuresEnd', ERROR_MESSAGES.dateRange, (value, context) =>
      context?.parent?.orderTypes.includes(ORDER_TYPE_NAMES.FUTURES) ? context?.parent?.futuresStart <= value : true
    ),
  forecastCrd: string().when('orderTypes', {
    is: orderTypes => orderTypes.includes(ORDER_TYPE_NAMES.FORECAST),
    then: string().required(forecastCRD)
  }),
  forecastStart: string().when('orderTypes', {
    is: orderTypes => orderTypes.includes(ORDER_TYPE_NAMES.FORECAST),
    then: string().required(forecastStart)
  }),
  forecastEnd: string()
    .when('orderTypes', {
      is: orderTypes => orderTypes.includes(ORDER_TYPE_NAMES.FORECAST),
      then: string().required(forecastEnd)
    })
    .test('forecastEnd', dateRange, (value, context) =>
      context?.parent?.orderTypes.includes(ORDER_TYPE_NAMES.FORECAST) ? context?.parent?.forecastStart <= value : true
    )
});

export const CATALOG_STATUS = {
  DELETED: 'DELETED',
  UNINDEX: 'UNINDEX',
  INDEX: 'INDEX'
};

// To be removed once service to retrieve sales orgs developed
export const SALES_ORGS = [
  {
    label: '1000 NIKE US Sls Org',
    value: '1000'
  },
  {
    label: '1020 North America',
    value: '1020'
  },
  {
    label: '1100 NIKE Canada Sls Org',
    value: '1100'
  },
  {
    label: '2000 NEON Netherlands',
    value: '2000'
  },
  {
    label: '9600 NIKE Hong Kong',
    value: '9600'
  },
  {
    label: '9700 NIKE Taiwan',
    value: '9700'
  },
  {
    label: '9800 NIKE China',
    value: '9800'
  },
  {
    label: '8600 New Zealand',
    value: '8600'
  },
  {
    label: '8700 Australia',
    value: '8700'
  },
  {
    label: '7000 Japan',
    value: '7000'
  },
  {
    label: '9900 Mexico',
    value: '9900'
  },
  {
    label: '8800 Korea',
    value: '8800'
  }
];

export const SALES_ORG_TO_REGION = {
  1000: 'USA',
  1020: 'USA_SEC',
  1100: 'CANADA',
  2000: 'EURO',
  7000: 'JAPAN',
  8600: 'NEW ZEALAND',
  8700: 'AUSTRALIA',
  8800: 'REP OF KOREA',
  9600: 'HONG KONG',
  9700: 'TAIWAN',
  9800: 'CHINA',
  9900: 'MEXICO'
};

// Define the maximum number of catalogs that can be indexed at once
export const MAX_CATALOG_INDEX = 100;
